import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Users, TrendingUp, TrendingDown, Activity, DollarSign, Award } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightsDashboard = ({ getAuthHeaders }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/insights/summary`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load insights');
      console.error('Insights error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <Card className="p-12 text-center">
        <p className="text-gray-500">No insights data available</p>
      </Card>
    );
  }

  const statusCards = [
    {
      label: 'Active Customers',
      value: summary.active_customers,
      total: summary.total_customers,
      icon: Activity,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      percentage: ((summary.active_customers / summary.total_customers) * 100).toFixed(0)
    },
    {
      label: 'Dormant Customers',
      value: summary.dormant_customers,
      total: summary.total_customers,
      icon: TrendingDown,
      color: 'from-yellow-500 to-yellow-600',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600',
      percentage: ((summary.dormant_customers / summary.total_customers) * 100).toFixed(0)
    },
    {
      label: 'Lost Customers',
      value: summary.lost_customers,
      total: summary.total_customers,
      icon: TrendingDown,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-100',
      textColor: 'text-red-600',
      percentage: ((summary.lost_customers / summary.total_customers) * 100).toFixed(0)
    },
    {
      label: 'New Customers',
      value: summary.new_customers,
      total: summary.total_customers,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      percentage: ((summary.new_customers / summary.total_customers) * 100).toFixed(0)
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>Customer Intelligence</h2>
        <p className="text-gray-500">Insights and analytics to drive your business decisions</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statusCards.map((card, index) => (
          <Card key={index} className="p-6 hover:shadow-lg transition-shadow" data-testid={`insight-card-${card.label.toLowerCase().replace(' ', '-')}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color}`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
              <Badge className={card.bgColor + ' ' + card.textColor}>
                {card.percentage}%
              </Badge>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{card.label}</p>
              <p className="text-3xl font-bold" data-testid={`${card.label.toLowerCase().replace(' ', '-')}-count`}>{card.value}</p>
              <p className="text-xs text-gray-400 mt-1">of {card.total} total</p>
            </div>
          </Card>
        ))}
      </div>

      {/* Revenue Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Total Lifetime Revenue</h3>
              <p className="text-sm text-gray-500">Across all customers</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-green-600" data-testid="total-lifetime-revenue">
            £{summary.total_lifetime_revenue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Average Customer Value</span>
              <span className="font-bold" data-testid="avg-customer-value">
                £{summary.average_customer_value.toFixed(2)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Award className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold">High-Value Services</h3>
              <p className="text-sm text-gray-500">Customers with premium services</p>
            </div>
          </div>
          <p className="text-4xl font-bold text-purple-600" data-testid="high-value-customers">
            {summary.customers_with_high_value_services}
          </p>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Ceramic coating, Lanoguard, and other premium services
            </p>
          </div>
        </Card>
      </div>

      {/* Top Customers */}
      <Card className="p-6">
        <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>Top 10 Customers by Spend</h3>
        <div className="space-y-3">
          {summary.top_customers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No customer data yet</p>
          ) : (
            summary.top_customers.map((customer, index) => (
              <div
                key={customer.customer_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid={`top-customer-${index}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-bold">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.job_count} jobs completed</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600">
                    £{customer.lifetime_spend.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-gray-500">lifetime value</p>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-yellow-50 border-yellow-200">
          <h4 className="font-bold text-yellow-900 mb-2">⚠️ Attention Needed</h4>
          <p className="text-sm text-yellow-800 mb-3">
            You have <strong>{summary.dormant_customers}</strong> dormant customers who haven't booked in 6-12 months.
          </p>
          <button className="text-sm text-yellow-700 underline hover:text-yellow-900">
            View dormant customers →
          </button>
        </Card>

        <Card className="p-6 bg-red-50 border-red-200">
          <h4 className="font-bold text-red-900 mb-2">🚨 Win-Back Opportunity</h4>
          <p className="text-sm text-red-800 mb-3">
            <strong>{summary.lost_customers}</strong> customers haven't booked in over a year.
          </p>
          <button className="text-sm text-red-700 underline hover:text-red-900">
            View lost customers →
          </button>
        </Card>

        <Card className="p-6 bg-green-50 border-green-200">
          <h4 className="font-bold text-green-900 mb-2">✅ Keep Them Happy</h4>
          <p className="text-sm text-green-800 mb-3">
            <strong>{summary.active_customers}</strong> active customers are regularly booking.
          </p>
          <button className="text-sm text-green-700 underline hover:text-green-900">
            View active customers →
          </button>
        </Card>
      </div>
    </div>
  );
};

export default InsightsDashboard;
